import React, { PureComponent } from "react";
import { validateFileExts, validateImageDimensions } from "../../utils";

export type Attachment = string | File;
interface ChildProps {
  onItemRemove: (index: number) => void;
  attachments: Attachment[];
  loading?: boolean;
  error: boolean;
  errorMsg: string;
  accept?: string;
  multiple?: boolean;
  handleFileChange: (e: any) => void;
}
interface Props {
  onChange: (data: Attachment[]) => void;
  maxNoOfSelections?: number;
  loading?: boolean;
  accept?: string;
  multiple?: boolean;
  allowedFileTypes?: string[];
  children: (props: ChildProps) => any;
  initialAttachments?: Attachment[];
  maxFileSize?: number; // In bytes
  minFileSize?: number; // In bytes
  maxDimensions?: { width: number; height: number };
}

interface State {
  attachments: Attachment[];
  error: boolean;
  errorMsg: string;
}

class MyPATUploader extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      attachments: props.initialAttachments ? props.initialAttachments : [],
      error: false,
      errorMsg: "",
    };
  }
  componentDidUpdate(prevProps: Props) {
    if (prevProps.initialAttachments !== this.props.initialAttachments) {
      if (this.props.initialAttachments)
        this.setState({ attachments: this.props.initialAttachments });
    }
  }

  validate = async (newAttachments: Attachment[]) => {
    const { allowedFileTypes, maxFileSize, maxDimensions, minFileSize } = this.props;

    const promiseArr = newAttachments.map(async (attachment) => {
      let isValid = true;
      let errorMsg = '';
      if (typeof attachment === "string") {
        return {isValid, errorMsg};
      }
      const { type, size } = attachment;

      // Check for fileTypes
      if (allowedFileTypes) {
        isValid = isValid && validateFileExts(type, allowedFileTypes);
        if(!isValid) {
          errorMsg = `Only ${allowedFileTypes.join(", ")} are allowed.`
        }
      }
      console.log({ size, minFileSize, maxFileSize })
      if(minFileSize!==undefined) {
        const isGreaterThanMinSize = size >= minFileSize
        isValid = isValid && isGreaterThanMinSize;
        if(!isGreaterThanMinSize) {
          errorMsg = `Files with size of atleast ${minFileSize/1024} KB are allowed`;
        }
      }

      // Check for size
      if (maxFileSize !== undefined) {
        const isLessThanMaxSize = size <= maxFileSize
        isValid = isValid && isLessThanMaxSize;
        if(!isLessThanMaxSize) {
          errorMsg = `Files with size less than ${maxFileSize/1024/1024} MB are allowed`;
        }
      }

      // Check for dimenstions
      if (maxDimensions) {
        const { width: maxWidth, height: maxHeight } = maxDimensions;
        try {
          await validateImageDimensions(attachment, maxWidth, maxHeight);
        } catch (error) {
          console.error(error);
          isValid = false;
          errorMsg = `Files within ${maxWidth} x ${maxHeight} pixels are allowed.`
          return {isValid, errorMsg};
        }
      }

      return {isValid, errorMsg};
    });
    try {
      const validationResults = await Promise.all(promiseArr);
      const isValid = validationResults.reduce((acc: boolean, { isValid }) => acc && isValid, true);
      const errorMsg = validationResults.map(({ errorMsg }) => errorMsg);
      return { isValid, errorMsg }
    } catch (error) {
      return { isValid: false, errorMsg: ['Some error occured'] };
    }
  };

  handleAttachmentsOnChange = async (e: any) => {
    const { maxNoOfSelections, allowedFileTypes } = this.props;
    const files: File[] = Array.from(e.target.files);

    if (files.length === 0) {
      return;
    }

    const newAttachments = this.state.attachments.concat(files);

    if (
      maxNoOfSelections !== undefined &&
      newAttachments.length > maxNoOfSelections
    ) {
      this.setState({
        error: true,
        errorMsg: `You can only select ${maxNoOfSelections} attachments.`,
      });
    } else {
      const { isValid, errorMsg } = await this.validate(newAttachments);

      if (!isValid && allowedFileTypes) {
        this.setState({
          error: true,
          errorMsg: errorMsg.join(', '),
        });
      } else {
        this.setState(
          {
            error: false,
            errorMsg: "",
            attachments: newAttachments,
          },
          () => this.props.onChange(this.state.attachments)
        );
      }
    }
  };

  handleRemovingAttachment = (i: number) => {
    this.setState(
      (prevState) => {
        const attachments = prevState.attachments.filter(
          (_, index) => index !== i
        );
        return {
          ...prevState,
          attachments,
          error: false,
          errorMsg: "",
        };
      },
      () => this.props.onChange(this.state.attachments)
    );
  };

  render() {
    const { error, errorMsg, attachments } = this.state;
    const { loading, accept, multiple } = this.props;
    return this.props.children({
      error,
      errorMsg,
      attachments,
      loading,
      onItemRemove: this.handleRemovingAttachment,
      handleFileChange: this.handleAttachmentsOnChange,
      multiple,
      accept,
    });
  }
}

export default MyPATUploader;
